import { Alert, Box, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as userGroupService from "../../../models/services/userGroupService";
import { IUserListItem, UserListItem } from "../../../models/user/userListItem";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { SelectUserDialog } from "../../user/SelectUserDialog/SelectUserDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IUserGroupEditDialogUserListProp {
    isReadOnly: boolean;
    userGroupId: string;
    companyId: string;
    qrCodeUserGroup: boolean;
    users: IUserListItem[];
    onRemoved: (ids: string[]) => void;
    onAdded: (users: IUserListItem[]) => void;
    onRefreshSessionState: () => void;
}

interface IUserGroupEditDialogUserListState {
    // List
    isLoading: boolean;
    users: IUserListItem[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectUserDialog: boolean;
}

export class UserGroupEditDialogUserList extends React.Component<IUserGroupEditDialogUserListProp, IUserGroupEditDialogUserListState> {
    constructor(props: IUserGroupEditDialogUserListProp) {
        super(props);
        this.state = {
            isLoading: false,
            users: props.users ? props.users.slice(0) : [],
            filter: "",
            sortColumn: "code",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectUserDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const props = this.props;
        let users = props.users ? props.users.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            users = users.filter(i => i.code.toLowerCase().indexOf(lowFilter) > -1 || i.firstName.toLowerCase().indexOf(lowFilter) > -1 || i.lastName.toLowerCase().indexOf(lowFilter) > -1);
        }
        UserListItem.sortUserListItems(users, column, asc);
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            users,
        });
    };

    componentDidUpdate(prevProps: IUserGroupEditDialogUserListProp, prevState: IUserGroupEditDialogUserListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.users ? prevProps.users.map(i => i.id) : [], props.users ? props.users.map(i => i.id) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IUserGroupEditDialogUserListProp, userIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.userGroupId);
        result.append("companyId", props.companyId);
        result.append("userIds", userIds);
        return result;
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const saveData = this.getRemoveSaveData(props, selectedIds);
        // Call server
        const result = await AppUtils.callService(() => userGroupService.userGroupEditRemoveUsers(saveData.obj));
        if (!result) return;
        props.onRemoved(selectedIds);
        if (result.refreshSessionState) {
            props.onRefreshSessionState();
        }
    };
    
    handleAdd = () => {
        this.setState({
            showSelectUserDialog: true
        });
    };

    getAddSaveData = (props: IUserGroupEditDialogUserListProp, userIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.userGroupId);
        result.append("companyId", props.companyId);
        result.append("userIds", userIds);
        return result;
    };

    onSelectUserDialogOk = async (users: IUserListItem[]) => {
        const props = this.props;
        if (!users || users.length < 1) return;
        this.setState({
            showSelectUserDialog: false
        });
        const saveData = this.getAddSaveData(this.props, users.map(i => i.id));
        // Call server
        const result = await AppUtils.callService(() => userGroupService.userGroupEditAddUsers(saveData.obj));
        if (!result) return;
        props.onAdded(users);
        if (result.refreshSessionState) {
            props.onRefreshSessionState();
        }
    };

    onSelectUserDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <VerticalContainer
                    before={<>
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter} />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddUser}
                                    removeTooltip={Translations.RemoveUser}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                        {!!props.isReadOnly &&
                            <Alert severity="info">{Translations.NewUserGroupMustBeSavedBeforeUsersCanBeAdded}</Alert>
                        }
                    </>}
                    main={<>
                        {!props.isReadOnly &&
                            <List
                                intialized={true}
                                isLoading={state.isLoading}
                                hasMore={false}
                                globalLoadingInUse={false}
                                height="100%"
                                header={{
                                    columns: [
                                        { size: 6, column: "code", content: Translations.UserName },
                                        { size: 6, column: "name", content: Translations.Name }
                                    ]
                                }}
                                lines={
                                    state.users.map((user) => {
                                        return {
                                            columns: [
                                                {
                                                    size: 6,
                                                    column: "code",
                                                    content:
                                                        <Box display="flex" alignItems="center">
                                                            <AvatarImage
                                                                id={user.code}
                                                                name={user.getName()}
                                                                image={user.picture}
                                                                tooltip={user.getName()}
                                                            />
                                                            <Typography variant="body2" sx={{ marginLeft: ".5rem" }}>{user.code}</Typography>
                                                        </Box>
                                                },
                                                { size: 6, column: "name", content: user.getName() }
                                            ],
                                            id: user.id,
                                            classes: user.disabled ? "disabled" : ""
                                        };
                                    })
                                }
                                noDataMessage={Translations.AddUsers}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                            />
                        }
                    </>}
                />
                {state.showSelectUserDialog &&
                    <SelectUserDialog
                        companyId={props.companyId}
                        multiSelect={true}
                        ignoreUserIds={props.users.map(i => i.id)}
                        ignoreQrCodeUsers={!props.qrCodeUserGroup}
                        requireQrCodeUsers={props.qrCodeUserGroup}
                        onOk={this.onSelectUserDialogOk}
                        onCancel={this.onSelectUserDialogCancel}
                    />
                }
            </>
        );
    }
}